import React from 'react';
import {
  Button,
  Container,
  Popup,
} from '@mairistem/composants';
import List from 'rc-virtual-list';
import {
  Exercice, Imputation, ImputationSelectionModal, LigneMouvement, Nature, Section, Tiers, useCodeTva,
  type Sens,
} from '@mairistem/comptabilite';
import { isNewIdentifiant, newIdentifiant } from '@mairistem/tools';
import { maxBy, isEmpty } from 'lodash';
import { Icon } from '@mairistem/finances-utils';
import LignesPiecesControl from './LignesPiecesControl';
import BaseProfilEcritureMouvement from
  '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import DefaultAttributes from '../../../../interfaces/pieces/mouvements/defaultAttributes';
import PanelOptions from '../../../../interfaces/pieces/mouvements/panelOptions';
import NumberOrString from '../../../../interfaces/common/numberOrString';
import './LignesPieces.less';

const MIN_LIGNES_INFINITE_LIST = 10;

interface SelectedImputation {
  imputation: Partial<Imputation>;
}

interface LignesPiecesProps {
  codeBHM: string;
  defaultAttributes: DefaultAttributes;
  deletedLignes: number[],
  exercice: Exercice;
  identifiantEntite: number;
  lignes: LigneMouvement[];
  onChange: any;
  panelOptions: PanelOptions;
  profilEcriture: BaseProfilEcritureMouvement;
  sens: Sens;
  setPanelOptions: React.Dispatch<React.SetStateAction<PanelOptions>>
  tiers: Tiers;
}

const LignesPieces = ({
  codeBHM,
  defaultAttributes,
  deletedLignes = [],
  exercice,
  identifiantEntite,
  lignes,
  onChange,
  panelOptions,
  profilEcriture,
  sens,
  setPanelOptions,
  tiers,
}: LignesPiecesProps) => {
  const [accordionActive, setAccordionActive] = React.useState(false);
  const [currentEditedLineId, setCurrentEditedLineId] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  // Liste des lignes ouvertes
  const [openedLignes, setOpenedLignes] = React.useState<NumberOrString[]>([]);

  const isInfiniteList = lignes?.length > MIN_LIGNES_INFINITE_LIST;

  const [loadingCodesTva, codesTva] = useCodeTva();

  const handleLineChange = (identifiantLigne: number | string, changes: object) => {
    onChange?.({
      lignes: lignes.map((ligne) => ({
        ...ligne,
        ...(ligne?.identifiant === identifiantLigne && changes),
      })),
    });
  };

  // Template d'une nouvelle ligne.
  const renderNewLine = (props?: Partial<LigneMouvement>) => {
    const maxIdLigne = maxBy(lignes, (ligne) => Number(ligne?.idLigne))?.idLigne;

    return {
      identifiant: newIdentifiant(),
      identifiantTiers: tiers?.identifiant ?? null,
      idLigne: (Number(maxIdLigne) || 0) + 1,
      ...(defaultAttributes ?? {}),
      ...props,
    };
  };

  const addNewLine = () => {
    onChange?.({
      lignes: [
        ...(lignes ?? []),
        renderNewLine(),
      ],
    });
  };

  const removeLine = (identifiantLigne: number | string) => {
    onChange?.({
      lignes: lignes?.filter((ligne) => ligne.identifiant !== identifiantLigne),
      ...(!isNewIdentifiant(identifiantLigne) && { deletedLignes: [...deletedLignes, identifiantLigne] }),
    });
  };

  const handleOpen = (id: number | string) => {
    setOpen(true);
    setCurrentEditedLineId(id);
  };

  const handleSelection = (imputations: SelectedImputation[]) => {
    const firstImputation = imputations[0].imputation;
    setOpen(false);

    const newLignes: LigneMouvement[] = [];

    if (imputations.length > 1) {
      // Création de nouvelles lignes pour chaque imputation sauf la ligne selectionnée
      let actualMaxIdLigne: number | null = null;
      imputations.slice(1).forEach((imputationData) => {
        const { imputation } = imputationData;
        const nouvelleLigne: LigneMouvement = renderNewLine({
          identifiantImputation: imputation?.identifiant,
          imputation: imputation as Imputation,
          // Mise à jour de l'idLigne pour qu'il soit unique
          ...{ ...(actualMaxIdLigne !== null && { idLigne: actualMaxIdLigne + 1 }) },
        }) as LigneMouvement;
        newLignes.push(nouvelleLigne);
        actualMaxIdLigne = nouvelleLigne.idLigne;
      });
    }

    onChange?.({
      lignes: [
        ...lignes.map((ligne) => ({
          ...ligne,
          ...(ligne?.identifiant === currentEditedLineId && {
            identifiantImputation: firstImputation?.identifiant,
            imputation: firstImputation,
          }),
        })),
        ...newLignes,
      ],
    });
  };

  const handleAccordion = () => setAccordionActive((old: boolean) => !old);

  const developedView = () => (
    lignes?.length > 0 && lignes?.length <= MIN_LIGNES_INFINITE_LIST && (
      <Container className="vueDeveloppee" fluid>
        <Button
          content={`Vue ${accordionActive ? 'simple' : 'développée'}`}
          icon={`angle ${accordionActive ? 'up' : 'down'}`}
          labelPosition="left"
          onClick={handleAccordion}
          size="tiny"
        />
      </Container>
    )
  );
  const renderLigne = (ligne: LigneMouvement) => (
    <LignesPiecesControl
      accordionActive={accordionActive}
      codeBHM={codeBHM}
      // @ts-expect-error
      codesTva={codesTva}
      exercice={exercice}
      identifiantEntite={identifiantEntite}
      isInfiniteList={isInfiniteList}
      isLigneOpen={!!openedLignes.find((id) => id === ligne?.identifiant)}
      key={ligne?.identifiant}
      ligne={ligne}
      // @ts-expect-error boolean
      loadingCodesTva={loadingCodesTva}
      onChange={handleLineChange}
      onDelete={removeLine}
      panelOptions={panelOptions}
      profilEcriture={profilEcriture}
      sens={sens as Sens}
      setModalOpen={handleOpen}
      setOpenedLignes={setOpenedLignes}
      setPanelOptions={setPanelOptions}
    />
  );

  return (
    <>
      {developedView()}
      {
        isInfiniteList
          ? (
            <List
              data={lignes}
              height={500}
              itemHeight={120}
              itemKey="identifiant"
            >
              {(item) => renderLigne(item)}
            </List>
          )
          : lignes?.map((ligne) => renderLigne(ligne))
      }
      {developedView()}
      <Container textAlign="center">
        <Popup
          content="Nouvelle ligne"
          position="bottom center"
          trigger={(
            <Icon
              link
              name="add"
              circular
              size="large"
              onClick={addNewLine}
            />
          )}
        />
      </Container>
      <ImputationSelectionModal
        articles={profilEcriture?.imputation?.articles}
        chapitre={profilEcriture?.imputation?.chapitre}
        disabledChapitre={!isEmpty(profilEcriture?.imputation?.chapitre)}
        disabledSection={!isEmpty(profilEcriture?.imputation?.section)}
        exercice={exercice}
        identifiantTiers={tiers?.identifiant ?? null}
        nature={profilEcriture?.imputation?.nature as Nature}
        onValidate={handleSelection}
        open={open}
        setOpen={setOpen}
        section={profilEcriture?.imputation?.section as Section}
        sens={sens}
      />
    </>
  );
};

export default LignesPieces;
